import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  ArrowRight, 
  Leaf, 
  Lightbulb, 
  Target, 
  Users,
  CheckCircle2,
  Award
} from "lucide-react";

const values = [
  {
    icon: Lightbulb,
    title: "Innovation",
    description: "Pioneering advanced materials technology to solve infrastructure challenges"
  },
  {
    icon: Leaf,
    title: "Sustainability",
    description: "Creating solutions that reduce environmental impact and promote green building"
  },
  {
    icon: Target,
    title: "Precision",
    description: "Engineering excellence with rigorous quality control and testing standards"
  },
  {
    icon: Users,
    title: "Partnership",
    description: "Collaborating with clients to deliver tailored solutions for complex projects"
  }
];

const credentials = [
  "IIT Hyderabad Tested & Certified",
  "ISO Quality Standards",
  "LEED Contribution Certified",
  "BIS Standards Compliant"
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="bg-white border-b border-slate-200" data-testid="about-hero">
        <div className="section-container py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="accent-line mb-6"></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 font-['Barlow_Condensed']">
              About BuildEdge Tech
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl">
              Leading the transition to next-generation infrastructure materials 
              with advanced GFRP solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="section-spacing bg-white" data-testid="mission-section">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="text-sm font-mono text-yellow-600 uppercase tracking-wider">
                About Us
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-['Barlow_Condensed'] mt-2 mb-6">
                Engineering Tomorrow's Infrastructure Today
              </h2>
              <p className="text-slate-600 mb-4 leading-relaxed">
                BuildEdge Tech is a forward-thinking engineering solutions company focused on delivering 
                innovative and reliable construction materials for modern infrastructure. We specialize in 
                advanced reinforcement solutions, including GFRP rebars and meshes, designed to enhance 
                durability, reduce maintenance, and improve the long-term performance of concrete structures.
              </p>
              <p className="text-slate-600 mb-4 leading-relaxed">
                With a strong commitment to quality and engineering excellence, BuildEdge Tech serves a 
                wide range of sectors including highways, industrial flooring, precast structures, marine 
                works, water treatment facilities, and commercial developments. Our solutions are developed 
                to meet the evolving demands of the construction industry by offering corrosion-resistant, 
                lightweight, and high-strength alternatives to conventional materials.
              </p>
              <p className="text-slate-600 mb-4 leading-relaxed">
                At BuildEdge Tech, we believe in building long-term partnerships with contractors, 
                consultants, and developers by providing technical support, practical guidance, and 
                dependable supply. Our team works closely with clients to understand project requirements 
                and recommend efficient, cost-effective reinforcement solutions.
              </p>
              <p className="text-slate-600 mb-6 leading-relaxed font-medium">
                Driven by innovation and guided by engineering expertise, BuildEdge Tech aims to contribute 
                to stronger, safer, and more sustainable infrastructure for the future.
              </p>
              <div className="accent-line"></div>
            </div>
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_nextgen-gfrp/artifacts/3ay0m7zw_mall2-2.webp"
                alt="Modern infrastructure construction"
                className="w-full h-[500px] object-cover border border-slate-200"
              />
              <div className="absolute -bottom-6 -left-6 bg-yellow-400 p-6">
                <p className="text-slate-900 font-bold font-['Barlow_Condensed'] text-2xl">100+</p>
                <p className="text-slate-700 text-sm">Year Design Life</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Expert Team Section */}
      <section className="section-spacing bg-white" data-testid="team-section">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Image Side */}
            <div className="relative order-2 lg:order-1">
              <div className="relative">
                <img 
                  src="https://images.pexels.com/photos/8487766/pexels-photo-8487766.jpeg"
                  alt="Expert Engineer"
                  className="w-full h-[500px] object-cover object-top"
                />
                {/* Decorative elements */}
                <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-yellow-400 -z-10"></div>
                <div className="absolute -top-4 -left-4 w-24 h-24 border-4 border-yellow-400 -z-10"></div>
              </div>
            </div>
            
            {/* Content Side */}
            <div className="order-1 lg:order-2">
              <span className="text-sm font-mono text-yellow-600 uppercase tracking-wider">
                Our Expertise
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-['Barlow_Condensed'] mt-2 mb-6">
                Backed by Industry Experts
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Our team comprises experienced civil engineers, material scientists, and construction 
                specialists who bring decades of expertise to every project. We understand the 
                challenges of modern infrastructure and deliver solutions that exceed expectations.
              </p>
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="border-l-4 border-yellow-400 pl-4">
                  <p className="text-3xl font-bold text-slate-900 font-['Barlow_Condensed']">15+</p>
                  <p className="text-slate-600 text-sm">Years Experience</p>
                </div>
                <div className="border-l-4 border-yellow-400 pl-4">
                  <p className="text-3xl font-bold text-slate-900 font-['Barlow_Condensed']">500+</p>
                  <p className="text-slate-600 text-sm">Projects Delivered</p>
                </div>
                <div className="border-l-4 border-yellow-400 pl-4">
                  <p className="text-3xl font-bold text-slate-900 font-['Barlow_Condensed']">50+</p>
                  <p className="text-slate-600 text-sm">Expert Engineers</p>
                </div>
                <div className="border-l-4 border-yellow-400 pl-4">
                  <p className="text-3xl font-bold text-slate-900 font-['Barlow_Condensed']">24/7</p>
                  <p className="text-slate-600 text-sm">Technical Support</p>
                </div>
              </div>
              <div className="accent-line"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-spacing bg-slate-50" data-testid="values-section">
        <div className="section-container">
          <div className="text-center mb-16">
            <div className="accent-line mx-auto mb-6"></div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-['Barlow_Condensed']">
              Our Values
            </h2>
            <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
              The principles that guide our work and drive our commitment to excellence
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div 
                key={index}
                className="bg-white border border-slate-200 p-8 card-hover"
                data-testid={`value-card-${index}`}
              >
                <div className="w-14 h-14 bg-yellow-400 flex items-center justify-center mb-6">
                  <value.icon className="w-7 h-7 text-slate-900" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 font-['Barlow_Condensed'] mb-3">
                  {value.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Credentials Section */}
      <section className="section-spacing bg-slate-900 relative noise-overlay" data-testid="credentials-section">
        <div className="section-container relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="accent-line mb-6"></div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white font-['Barlow_Condensed'] mb-6">
                Trusted Quality & Credentials
              </h2>
              <p className="text-slate-400 mb-8 leading-relaxed">
                Our products undergo rigorous testing and meet international quality standards. 
                BuildEdge Tech is your trusted partner for certified GFRP solutions.
              </p>
              <ul className="space-y-4">
                {credentials.map((credential, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                    <span className="text-white">{credential}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex justify-center">
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-slate-800 border border-slate-700 p-8 text-center">
                  <Award className="w-10 h-10 text-yellow-400 mx-auto mb-4" />
                  <p className="text-white font-bold font-['Barlow_Condensed'] text-lg">IIT Tested</p>
                </div>
                <div className="bg-slate-800 border border-slate-700 p-8 text-center">
                  <Award className="w-10 h-10 text-yellow-400 mx-auto mb-4" />
                  <p className="text-white font-bold font-['Barlow_Condensed'] text-lg">ISO Certified</p>
                </div>
                <div className="bg-slate-800 border border-slate-700 p-8 text-center">
                  <Leaf className="w-10 h-10 text-yellow-400 mx-auto mb-4" />
                  <p className="text-white font-bold font-['Barlow_Condensed'] text-lg">LEED Ready</p>
                </div>
                <div className="bg-slate-800 border border-slate-700 p-8 text-center">
                  <Target className="w-10 h-10 text-yellow-400 mx-auto mb-4" />
                  <p className="text-white font-bold font-['Barlow_Condensed'] text-lg">BIS Compliant</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Applications Link Section */}
      <section className="section-spacing bg-slate-50" data-testid="applications-link">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm font-mono text-yellow-600 uppercase tracking-wider">
                Explore Use Cases
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-['Barlow_Condensed'] mt-2 mb-6">
                Discover GFRP Applications
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                From highways and bridges to hospitals and water treatment plants, GFRP reinforcement 
                is transforming infrastructure across 24+ application areas. Explore how our PRO BAR 
                and PRO MESH solutions are being used in projects worldwide.
              </p>
              <Link to="/applications">
                <Button 
                  className="bg-yellow-400 text-slate-900 hover:bg-yellow-500 font-bold uppercase tracking-wider rounded-none px-8 py-6"
                  data-testid="about-applications-btn"
                >
                  View All Applications
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {["RCC Roads", "Bridge Decks", "Tunneling", "Chemical Plants", "Swimming Pools", "MRI Areas"].map((app, i) => (
                <div key={i} className="bg-white border border-slate-200 p-4 text-center hover:border-yellow-400 transition-colors">
                  <p className="text-slate-800 font-medium text-sm">{app}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing bg-yellow-400" data-testid="about-cta">
        <div className="section-container text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-['Barlow_Condensed'] mb-6">
            Partner With Us
          </h2>
          <p className="text-slate-700 max-w-2xl mx-auto mb-10">
            Join the growing number of infrastructure developers, contractors, and engineers 
            who trust BuildEdge Tech for their GFRP needs.
          </p>
          <Link to="/contact">
            <Button 
              className="bg-slate-900 text-white hover:bg-slate-800 font-bold uppercase tracking-wider rounded-none px-8 py-6"
              data-testid="about-contact-btn"
            >
              Get In Touch
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
